import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { 
  ArrowLeft, 
  Bot,
  Search,
  ChevronDown,
  ChevronUp,
  HelpCircle,
  Zap,
  Shield,
  CreditCard,
  Settings,
  Code,
  Users
} from 'lucide-react';
import { PublicHeader } from '@/components/common/PublicHeader';
import { PublicFooter } from '@/components/common/PublicFooter';

const FAQPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState({});
  const [activeCategory, setActiveCategory] = useState('all');

  const toggleItem = (id) => {
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const categories = [
    { id: 'all', label: 'All Questions', icon: HelpCircle },
    { id: 'getting-started', label: 'Getting Started', icon: Zap },
    { id: 'features', label: 'Features', icon: Settings },
    { id: 'pricing', label: 'Pricing & Billing', icon: CreditCard },
    { id: 'security', label: 'Security & Privacy', icon: Shield },
    { id: 'technical', label: 'Technical', icon: Code },
    { id: 'team', label: 'Teams & Collaboration', icon: Users }
  ];

  const faqs = [
    // Getting Started
    {
      id: 'what-is-harmonix',
      category: 'getting-started',
      question: 'What is Harmonix?',
      answer: 'Harmonix is an AI-powered platform for creating intelligent AI Employees. It helps businesses create smart AI employees that understand customer intent, answer questions accurately using your knowledge base, capture leads, and automate customer support 24/7. Powered by Google Gemini AI, Harmonix can be deployed on websites, WhatsApp, Facebook, and other channels.'
    },
    {
      id: 'how-does-harmonix-work',
      category: 'getting-started',
      question: 'How does Harmonix work?',
      answer: 'Harmonix works in 4 simple steps: 1) Create an AI Employee and give it a name, 2) Upload your documents (PDFs, Word files, text) or import content from website URLs - this becomes your knowledge base, 3) Customize the AI personality, welcome message, and appearance, 4) Copy the embed code and add it to your website. The AI automatically learns from your content and answers questions with source citations.'
    },
    {
      id: 'how-to-create-chatbot',
      category: 'getting-started',
      question: 'How do I create an AI Employee with Harmonix?',
      answer: 'Creating an AI Employee takes just minutes: Sign up for a free account, click "New AI Employee", upload your documents or paste website URLs, customize the welcome message and appearance, then copy the embed code to your website. No coding required - the AI handles everything automatically.'
    },
    {
      id: 'what-documents-supported',
      category: 'getting-started',
      question: 'What types of documents can I upload?',
      answer: 'Harmonix supports PDF files, Microsoft Word documents (.doc, .docx), plain text files (.txt), and images (.png, .jpg). You can also import content directly from any website URL or sitemap. The AI processes these documents and creates a searchable knowledge base for your AI Employee.'
    },
    // Features
    {
      id: 'conversation-intelligence',
      category: 'features',
      question: 'What is conversation intelligence?',
      answer: 'Conversation intelligence is Harmonix\'s ability to understand the context, intent, and meaning behind customer messages. Instead of just keyword matching, our AI comprehends what customers are actually asking, maintains conversation context across multiple messages, and provides relevant, accurate responses. This includes understanding follow-up questions, detecting sentiment, and identifying when to escalate to a human agent.'
    },
    {
      id: 'lead-capture',
      category: 'features',
      question: 'How does lead capture work?',
      answer: 'Harmonix captures leads through natural conversation rather than forcing users to fill out forms. The AI engages visitors, understands their needs, and collects contact information (name, email, company, requirements) naturally during the chat. All leads are stored in your dashboard with conversation history, and can be exported or synced with your CRM.'
    },
    {
      id: 'appointment-booking',
      category: 'features',
      question: 'Can the AI Employee book appointments?',
      answer: 'Yes! Harmonix integrates with Google Calendar and Apple Calendar. Enable appointment booking in your AI Employee settings, set your available hours and appointment duration, and the AI will handle scheduling. Customers can book directly through the chat, and both parties receive calendar invites automatically.'
    },
    {
      id: 'multi-channel',
      category: 'features',
      question: 'What channels does Harmonix support?',
      answer: 'Harmonix supports multiple channels: Website chat widget (embed anywhere), WhatsApp Business, Facebook Messenger, Instagram DM, and Telegram. All conversations are unified in one dashboard regardless of the channel they come from.'
    },
    {
      id: 'multiple-languages',
      category: 'features',
      question: 'Does Harmonix support multiple languages?',
      answer: 'Yes, Harmonix supports multiple languages. The AI can understand and respond in various languages based on your knowledge base content. You can create AI Employees in English, Spanish, French, German, and many other languages. The system automatically detects the customer\'s language and responds accordingly.'
    },
    {
      id: 'source-citations',
      category: 'features',
      question: 'Does the AI cite its sources?',
      answer: 'Yes! When the AI answers questions, it includes citations showing which documents or pages the information came from. This builds trust with users and allows them to verify information. You can customize how citations are displayed in your AI Employee settings.'
    },
    // Pricing & Billing
    {
      id: 'free-plan',
      category: 'pricing',
      question: 'Is there a free plan?',
      answer: 'Yes, Harmonix offers a free plan that includes 1 AI Employee, 100 messages per month, and basic features. It\'s perfect for trying out the platform or for small websites. No credit card required to start.'
    },
    {
      id: 'pricing-plans',
      category: 'pricing',
      question: 'What are the pricing plans?',
      answer: 'Harmonix offers flexible pricing: Free ($0/month - 1 AI Employee, 100 messages), Starter ($49/month - 3 AI Employees, 1000 messages), Growth ($99/month - 10 AI Employees, unlimited messages with CRM and automations), and Enterprise (custom pricing for large organizations). All paid plans include advanced features like appointment booking, multi-channel support, and priority support.'
    },
    {
      id: 'refund-policy',
      category: 'pricing',
      question: 'What is the refund policy?',
      answer: 'Customers are entitled to a full refund within 14 days of their initial purchase. After the 14-day period, subscription fees are non-refundable. Cancellations stop future charges but do not result in refunds for past payments. Refund requests can be made by contacting support@harmonix.info or through Paddle\'s support portal.'
    },
    {
      id: 'payment-methods',
      category: 'pricing',
      question: 'What payment methods are accepted?',
      answer: 'Payments are processed securely by Paddle, our merchant of record. Accepted payment methods include major credit/debit cards (Visa, Mastercard, American Express) and local payment methods depending on your region. All transactions are encrypted and secure.'
    },
    // Security & Privacy
    {
      id: 'data-security',
      category: 'security',
      question: 'How secure is my data?',
      answer: 'Harmonix takes security seriously. All data is encrypted in transit (TLS 1.3) and at rest (AES-256). We use secure cloud infrastructure, regular security audits, and follow industry best practices. Your documents and conversations are stored securely and never shared with third parties.'
    },
    {
      id: 'data-ownership',
      category: 'security',
      question: 'Who owns my data?',
      answer: 'You own all your data. This includes your uploaded documents, AI Employee configurations, conversation history, and lead information. You can export or delete your data at any time. We process your data only to provide the Harmonix service.'
    },
    {
      id: 'gdpr-compliant',
      category: 'security',
      question: 'Is Harmonix GDPR compliant?',
      answer: 'Yes, Harmonix is designed with privacy in mind and complies with GDPR requirements. We provide tools for data export, deletion requests, and consent management. Our cookie consent banner allows visitors to control their preferences.'
    },
    {
      id: 'ai-data-training',
      category: 'security',
      question: 'Is my data used to train AI models?',
      answer: 'No, your data is not used to train general AI models. Your documents and conversations are used only to power your specific AI Employee. We use Google Gemini AI for language understanding, but your content remains private and is not shared with or used to improve external AI systems.'
    },
    // Technical
    {
      id: 'embed-code',
      category: 'technical',
      question: 'How do I add the AI Employee to my website?',
      answer: 'Adding the AI Employee is simple: Go to your agent\'s Embed tab, copy the embed code (a single script tag), and paste it into your website\'s HTML just before the closing </body> tag. The chat widget will appear automatically. Any changes you make to settings apply instantly - no need to update the code.'
    },
    {
      id: 'api-access',
      category: 'technical',
      question: 'Does Harmonix have an API?',
      answer: 'Yes, Harmonix provides a REST API for integrating with your own applications. You can send messages, retrieve conversations, manage AI Employees, and access analytics programmatically. API documentation is available in your dashboard.'
    },
    {
      id: 'website-compatibility',
      category: 'technical',
      question: 'What websites is Harmonix compatible with?',
      answer: 'Harmonix works with any website - WordPress, Shopify, Wix, Squarespace, custom HTML, React, Vue, and more. The embed code is a simple JavaScript snippet that works universally. If your website can load JavaScript, it can run Harmonix.'
    },
    {
      id: 'update-without-reembed',
      category: 'technical',
      question: 'Do I need to update the embed code when I change settings?',
      answer: 'No! Once you add the embed code to your website, any changes you make in the Harmonix dashboard (knowledge base, appearance, settings) are applied automatically. The embed code stays the same forever.'
    },
    // Teams & Collaboration
    {
      id: 'team-access',
      category: 'team',
      question: 'Can multiple team members access the same AI Employees?',
      answer: 'Yes. System Admins invite sub-admins and moderators from the Admin → Users page. Each sub-admin inherits a role (System Admin, Moderator, User) and per-user permission overrides layer on top — so you can grant one teammate write access to a specific segment (e.g. Roles) without promoting them fully. Moderators are scoped to a single AI Employee (up to 2 per agent).'
    },
    {
      id: 'moderator-features',
      category: 'team',
      question: 'What can moderators do?',
      answer: 'Moderators can monitor live conversations, take over chats from the AI when needed, view conversation history, and manage leads. They receive notifications for new messages and can respond directly from the Harmonix dashboard or via email.'
    },
    {
      id: 'conversation-handoff',
      category: 'team',
      question: 'How does human handoff work?',
      answer: 'When the AI detects a complex question or the customer requests human help, it can automatically notify your team and hand off the conversation. Moderators see the full conversation history and can seamlessly continue helping the customer. You can also set up automatic handoff rules based on keywords or sentiment.'
    },
    // ── New iter122-125 additions ──
    {
      id: 'file-size-limit',
      category: 'features',
      question: 'What file sizes and types can I upload to the knowledge base?',
      answer: 'Each file must be 10 MB or smaller. Supported formats: PDF (.pdf), Word (.docx), plain text (.txt), CSV (.csv), and Markdown (.md). You can drop multiple files in a single upload and each one is validated on selection — unsupported types (.xlsx, .png, .jpg, .zip, etc.) are blocked immediately with a clear message so you never wait through a broken upload.'
    },
    {
      id: 'kb-privacy',
      category: 'security',
      question: 'Can visitors ask the AI to list every file in the knowledge base?',
      answer: 'No. The agent hard-refuses meta questions like "list all files", "how many documents do you have", "show me your first document", or "are you GPT?". These prompts are caught before retrieval so the AI never sees a filename to leak. Answers still include Source citations for legitimate business questions.'
    },
    {
      id: 'trusted-devices',
      category: 'security',
      question: 'How do I manage the devices signed into my account?',
      answer: 'Go to Settings → Security → Trusted Devices. Every device you sign in from shows up with the last-seen time, IP, and active session count. Star a device to trust it (skip new-device email alerts), un-trust to re-enable alerts, or revoke to instantly sign out every session on that device — perfect for a lost phone. You cannot revoke your current device; use the standard Sign Out button.'
    },
    {
      id: 'session-duration',
      category: 'security',
      question: 'How long do sign-in sessions last?',
      answer: 'Sessions follow GitHub-style rules: 14-day inactivity window, refreshed only when 7 days or fewer remain, and an absolute 30-day cap. Even if you\'re active every day, you\'ll be asked to re-authenticate after 30 days. You can review or revoke individual sessions in Settings → Security.'
    },
    {
      id: 'new-device-alert',
      category: 'security',
      question: 'Will I be notified if someone signs into my account from a new device?',
      answer: 'Yes. The first time an unrecognised device signs in, you receive an email with the approximate location, browser, and time. Trust the device from the alert to silence future emails for that device; leave it un-trusted to keep receiving alerts on every login.'
    },
    {
      id: 'oauth-cancel',
      category: 'technical',
      question: 'I clicked "Continue with Google" and then Cancel — why did I see a JSON error?',
      answer: 'That was a bug in an earlier build. As of the current release, cancelling on the Google consent screen returns you to the login page with a friendly "Google sign-in was cancelled. Please try again." message. If you still see a raw error, please contact support with a screenshot.'
    },
    {
      id: 'custom-vs-fixed',
      category: 'pricing',
      question: 'Are the fixed plans and the custom plan builder priced the same?',
      answer: 'They\'re related but not identical. Fixed plans (Starter, Growth, Enterprise) offer curated feature bundles at anchor prices. The custom builder on the pricing page lets you dial usage limits and receive a live price based on our base cost × profit margin. Admins get a Custom-vs-Fixed comparison card in the dashboard that flags whenever a fixed plan is priced the same or higher than the custom equivalent, so pricing stays coherent.'
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = activeCategory === 'all' || faq.category === activeCategory;
    const matchesSearch = searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <PublicHeader />

      {/* Hero Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 bg-slate-100 text-slate-900 rounded-full text-sm font-medium mb-6">
            HELP CENTER
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
            Everything you need to know about Harmonix. Can&apos;t find what you&apos;re looking for? Contact our support team.
          </p>
          
          {/* Search */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search questions..."
              className="pl-12 h-14 text-lg bg-white border-slate-200 rounded-xl shadow-sm"
            />
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-4 px-4 bg-slate-50 border-y border-slate-200 overflow-x-auto">
        <div className="max-w-6xl mx-auto flex gap-2 min-w-max">
          {categories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                activeCategory === cat.id 
                  ? 'bg-teal-600 text-white' 
                  : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }`}
            >
              <cat.icon className="w-4 h-4" />
              <span className="text-sm font-medium">{cat.label}</span>
            </button>
          ))}
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto space-y-4">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No questions found matching your search.</p>
            </div>
          ) : (
            filteredFaqs.map((faq) => (
              <Card 
                key={faq.id} 
                className={`bg-white border-slate-200 shadow-sm transition-all ${openItems[faq.id] ? 'ring-2 ring-slate-100' : ''}`}
              >
                <button
                  onClick={() => toggleItem(faq.id)}
                  className="w-full p-6 text-left flex items-start justify-between gap-4"
                >
                  <h3 className="font-semibold text-slate-900 text-lg pr-4">{faq.question}</h3>
                  {openItems[faq.id] ? (
                    <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
                  )}
                </button>
                {openItems[faq.id] && (
                  <CardContent className="px-6 pb-6 pt-0">
                    <p className="text-slate-600 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-4">
            Still have questions?
          </h2>
          <p className="text-slate-600 mb-8">
            Our support team is here to help. Get in touch and we&apos;ll respond as soon as possible.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => window.location.href = 'mailto:support@harmonix.info?subject=FAQ%20Question'} 
              size="lg"
              className="bg-teal-600 hover:bg-teal-700"
            >
              Contact Support
            </Button>
            <Button 
              onClick={() => navigate('/docs/user-manual')} 
              size="lg"
              variant="outline"
            >
              Read Documentation
            </Button>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default FAQPage;
